// Shared site-analysis module: groups raw input variables by component score
// (CS / RDS / HCO / HCC / NONRES) and provides helpers for the metrics drill-down panel.
//
// Ported from X:/projects/Remix of Tool/src/lib/siteAnalysis.ts
//
// OMITTED (deferred to Phase 2.2):
//   - loadStateMedians, buildSiteAnalysisReport, InputRow, InputGroupReport, buildPercentileSummary
//
// NOTE: tractField names match the CensusTract interface in tractModel.ts.
// The prototype used meanVehiclesPerHH / medianHHIncome; we normalise to
// meanVehiclesPerHh / medianHhIncome (standard camelCase) to match tractModel.ts.

import type { CensusTract } from './tractModel'
import type { GeoLevel } from './geoLevel'

export type FormatKind =
  'percent' | 'currency' | 'number' | 'decimal' | 'miles' | 'minutes' | 'per1k' | 'density' | 'raw'

export type GroupKey = 'CS' | 'RDS' | 'HCO' | 'HCC' | 'NONRES'

export interface InputVariable {
  /** Field on CensusTract (camelCase). */
  tractField: keyof CensusTract
  /** Field in state_medians_*.json (snake_case) — used by Phase 2.2 buildSiteAnalysisReport. */
  medianField: string
  label: string
  /** Brief plain-English what-it-means line. */
  description: string
  format: FormatKind
  /** "higher" → larger values are stronger for this group; "lower" → smaller is stronger; "neutral" → context-only. */
  direction: 'higher' | 'lower' | 'neutral'
  /**
   * T-537: the geography this metric is actually measured at — surfaced as a tag next to the
   * metric so a user knows whether a number describes this tract or a coarser area. Every value
   * below is traced to the pipeline stage that computes it (cited inline); see geoLevel.ts for
   * what a level means. NOT a guess: `county` is used only where the value is identical for every
   * tract in the county.
   */
  level: GeoLevel
  /**
   * T-166 (BEAD-0072) value color-coding basis. Only set to "share100" for metrics that are
   * genuine full-range 0–100% proportions of the tract's own population/housing, where the 0–100
   * scale is real and the `direction` says which end is favorable — so `favorableShare` +
   * `scoreColor` can band them good/mid/bad the same way the Scores section is banded.
   * DELIBERATELY LEFT UNSET (→ neutral, no invented threshold) for: absolute magnitudes
   * (population, income, densities, counts, distances), naturally-small job shares (0–10% range),
   * and port shares (confounded: a tract with no existing stations scores 0 by definition, so 0%
   * would wrongly read as "bad"). See ScoreDrillDown color rationale.
   */
  colorBasis?: 'share100'
}

export interface InputGroup {
  key: GroupKey
  title: string
  blurb: string
  variables: InputVariable[]
}

export const INPUT_GROUPS: InputGroup[] = [
  {
    key: 'CS',
    // LEVEL PROVENANCE (T-537): all tract. The port shares and interstate distance come from
    // port-level rows in `port_scored` aggregated with GROUP BY se.tract_geoid
    // (exports/tool_export.py::_compute_port_aggregations); corridor exposure is per-station,
    // averaged to the tract; nearest-DCFC distance is measured from the tract centroid
    // (enrichment/nearest_dcfc.py). Finer-than-tract SOURCES, but a per-tract RESULT.
    title: 'Corridor & Accessibility',
    blurb: 'How well-positioned this tract is along high-traffic EV travel corridors.',
    variables: [
      {
        tractField: 'corridorExposureScore',
        medianField: 'corridor_exposure_score_tract',
        label: 'Corridor exposure score',
        description:
          'Per-station corridor positioning, averaged across the tract. For tracts with no existing stations, falls back to the tract centroid.',
        format: 'decimal',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'avgDistInterstateMi',
        medianField: 'avg_dist_interstate_mi',
        label: 'Avg. distance to interstate',
        description:
          'Mean straight-line distance from existing ports to the nearest interstate. For tracts with no existing stations, measured from the tract centroid.',
        format: 'miles',
        direction: 'lower',
        level: 'tract',
      },
      {
        tractField: 'pctPortsNearRamp',
        medianField: 'pct_ports_near_ramp',
        label: '% ports near a ramp',
        description:
          'Share of existing ports located within ramp range of an interstate. Tracts with no existing stations score 0.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'pctPortsOnAfc',
        medianField: 'pct_ports_on_afc',
        label: '% ports on an AFC',
        description:
          'Share of existing ports sited on an Alternative Fuel Corridor. Tracts with no existing stations score 0.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'pctPortsAtRestArea',
        medianField: 'pct_ports_at_rest_area',
        label: '% ports at a rest area',
        description:
          'Share of existing ports located at highway rest areas. Tracts with no existing stations score 0.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
      },
    ],
  },
  {
    key: 'RDS',
    // LEVEL PROVENANCE (T-537): all tract. Job shares and job density come from LEHD LODES8 WAC
    // block records aggregated block → tract (enrichment/lodes_wac.py); the daytime population
    // ratio combines WAC with RAC the same way (enrichment/lodes_rac.py).
    title: 'Retail-Destination Strength',
    blurb: 'Attractiveness as a retail/commercial dwell-time destination.',
    variables: [
      {
        tractField: 'jobsRetailPct',
        medianField: 'jobs_retail_pct',
        label: 'Retail job share',
        description: 'Share of jobs in retail trade.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'jobsFoodAccommodationPct',
        medianField: 'jobs_food_accommodation_pct',
        label: 'Food & accommodation job share',
        description: 'Restaurants, cafés, hotels — strong dwell-time signal.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'jobsEntertainmentPct',
        medianField: 'jobs_entertainment_pct',
        label: 'Entertainment job share',
        description: 'Arts, recreation, entertainment — long visit windows.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'jobsHealthcarePct',
        medianField: 'jobs_healthcare_pct',
        label: 'Healthcare job share',
        description: 'Healthcare destinations — long predictable dwell time.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'jobsPerSqMile',
        medianField: 'jobs_per_sq_mile',
        label: 'Jobs per sq. mile',
        description: 'Overall employment density — proxy for daytime population.',
        format: 'density',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'daytimePopRatio',
        medianField: 'daytime_pop_ratio',
        label: 'Daytime population ratio',
        description: 'Daytime vs. residential population — net inflow indicator.',
        format: 'decimal',
        direction: 'higher',
        level: 'tract',
      },
    ],
  },
  {
    key: 'HCO',
    // LEVEL PROVENANCE (T-537): all tract. Straight from the ACS 5-year TRACT tables —
    // B25024 (structure type), B25003 (tenure), B08201 (vehicles), B08301 (commute mode),
    // B25077 (home value); see enrichment/acs_merge.py and enrichment/acs_additional.py.
    // NOTE: the parallel `*_bg` columns in enrichment/acs_block_group.py are a SEPARATE
    // block-group scoring path and are not what this card reads.
    title: 'Home-Charging Suitability',
    blurb: 'Share of residents who CAN charge at home — limits public-charging demand.',
    variables: [
      {
        tractField: 'sfDetachedShare',
        medianField: 'sf_detached_share',
        label: 'Single-family detached share',
        description: 'Share of housing units that are single-family detached homes.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
        colorBasis: 'share100',
      },
      {
        tractField: 'pctOwnerOccupied',
        medianField: 'pct_owner_occupied',
        label: 'Owner-occupied share',
        description: 'Share of households that own their home.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
        colorBasis: 'share100',
      },
      {
        tractField: 'meanVehiclesPerHh',
        medianField: 'mean_vehicles_per_hh',
        label: 'Mean vehicles per household',
        description: 'Average number of vehicles per household.',
        format: 'decimal',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'pctDroveAlone',
        medianField: 'pct_drove_alone',
        label: '% drove alone to work',
        description: 'Commute-mode share of solo drivers.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
        colorBasis: 'share100',
      },
      {
        tractField: 'medianHomeValue',
        medianField: 'median_home_value',
        label: 'Median home value',
        description: 'Proxy for ability to invest in home charging equipment.',
        format: 'currency',
        direction: 'higher',
        level: 'tract',
      },
    ],
  },
  {
    key: 'HCC',
    // LEVEL PROVENANCE (T-537): all tract. ACS 5-year TRACT tables — B25003 (tenure),
    // B25024 (units in structure), B08201 (vehicle availability), B08136/B08301 (travel time),
    // B25002 (vacancy). See enrichment/acs_merge.py and enrichment/acs_additional.py.
    title: 'Home-Charging Constrained',
    blurb: "Share of residents who CAN'T easily charge at home — drives public-charging demand.",
    variables: [
      {
        tractField: 'pctRenter',
        medianField: 'pct_renter',
        label: 'Renter share',
        description: 'Share of households that rent.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
        colorBasis: 'share100',
      },
      {
        tractField: 'multifamilyShare',
        medianField: 'multifamily_share',
        label: 'Multifamily housing share',
        description: 'Share of housing units in buildings with 2+ units.',
        format: 'percent',
        direction: 'higher',
        level: 'tract',
        colorBasis: 'share100',
      },
      {
        tractField: 'pctNoVehicle',
        medianField: 'pct_no_vehicle',
        label: '% households with no vehicle',
        description: 'Share of households without any vehicle.',
        format: 'percent',
        direction: 'neutral',
        level: 'tract',
      },
      {
        tractField: 'meanCommuteTime',
        medianField: 'mean_commute_time',
        label: 'Mean commute time (min)',
        description: 'Average one-way commute time.',
        format: 'minutes',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'vacancyRate',
        medianField: 'vacancy_rate',
        label: 'Housing vacancy rate',
        description: 'Share of housing units that are vacant.',
        format: 'percent',
        direction: 'neutral',
        level: 'tract',
      },
    ],
  },
  {
    key: 'NONRES',
    // LEVEL PROVENANCE (T-537): MIXED — the only group that is not uniformly tract-level.
    // Tract: ACS tract tables (population, income, college share), densities derived as
    // tract_pop / aland_sqmi and housing_units_total / aland_sqmi (enrichment/derived_features.py),
    // and the AFDC station/port counts tallied per tract.
    // County: EV registrations per 1k and 5-year population growth — see the per-metric notes.
    title: 'Context & Non-Residential',
    blurb: 'Population, density, EV adoption, and existing supply for context.',
    variables: [
      {
        tractField: 'population',
        medianField: 'tract_pop',
        label: 'Tract population',
        description: 'Total resident population.',
        format: 'number',
        direction: 'neutral',
        level: 'tract',
      },
      {
        tractField: 'populationDensity',
        medianField: 'population_density',
        label: 'Population density',
        description: 'Residents per square mile of land area.',
        format: 'density',
        direction: 'neutral',
        level: 'tract',
      },
      {
        tractField: 'householdDensity',
        medianField: 'household_density',
        label: 'Household density',
        description: 'Households per square mile of land area.',
        format: 'density',
        direction: 'neutral',
        level: 'tract',
      },
      {
        tractField: 'medianHhIncome',
        medianField: 'median_hh_income',
        label: 'Median household income',
        description: 'Higher income → more EV adoption headroom.',
        format: 'currency',
        direction: 'higher',
        level: 'tract',
      },
      {
        tractField: 'pctCollegeEducated',
        medianField: 'pct_college_educated',
        label: '% college educated',
        description: "Share of adults with a bachelor's degree or higher.",
        format: 'percent',
        direction: 'higher',
        level: 'tract',
        colorBasis: 'share100',
      },
      {
        tractField: 'evRegPer1000',
        medianField: 'ev_reg_per_1000',
        label: 'EV registrations per 1k',
        description: 'Local EV adoption per 1,000 residents, measured across the county.',
        format: 'per1k',
        direction: 'higher',
        // COUNTY, not tract: ev_registrations.py loads ZIP-level registrations and maps each ZIP
        // to a county FIPS via the Census ZCTA crosswalk, then merges the county rate onto every
        // tract in that county. Two tracts in the same county always show the same number.
        level: 'county',
      },
      {
        tractField: 'popGrowth5yr',
        medianField: 'pop_growth_5yr',
        label: '5-year population growth',
        description: 'Population change over the last 5 years, measured across the county.',
        format: 'percent',
        direction: 'higher',
        // COUNTY, not tract: county_growth.py computes (pop_2022 − pop_2017) / pop_2017 from
        // county-level ACS totals and says so outright — "each tract in a county receives the same
        // county-level growth rate".
        level: 'county',
      },
      {
        tractField: 'stationCount',
        medianField: 'station_count_tract',
        label: 'Existing stations',
        description: 'Total charging stations currently in the tract.',
        format: 'number',
        direction: 'neutral',
        level: 'tract',
      },
      {
        tractField: 'portsTotal',
        medianField: 'ports_total',
        label: 'Existing ports',
        description: 'Total charging ports across all stations.',
        format: 'number',
        direction: 'neutral',
        level: 'tract',
      },
      {
        tractField: 'dcfcPorts',
        medianField: 'dcfc_ports',
        label: 'Existing DC Fast ports',
        description: 'Total DC fast-charge ports.',
        format: 'number',
        direction: 'neutral',
        level: 'tract',
      },
      {
        tractField: 'stationsPer1kEv',
        medianField: 'stations_per_1k_ev',
        label: 'Stations per 1k EVs',
        description: 'Existing supply normalized by local EV count.',
        format: 'decimal',
        direction: 'neutral',
        // Hybrid, tagged at the level it RESOLVES to: derived_features.py computes
        // station_count / (ev_reg_per_1000 × tract_pop / 1000). The numerator and tract_pop are
        // tract-level so the result does vary tract to tract — but the EV rate in the denominator
        // is the county figure above. Tract is the honest tag; the county input is noted here.
        level: 'tract',
      },
    ],
  },
]

/**
 * T-537: the level for a metric identified by its CensusTract field.
 *
 * The Context section of the drill-down shows four metrics that ALSO appear in the All-metrics
 * card (population, density, income, EV registrations). Hardcoding their levels at the call site
 * would let the two copies of the same metric disagree — and one of them, EV registrations, is the
 * county metric this card exists to stop mislabelling. Reading from INPUT_GROUPS keeps one answer
 * per metric. Returns null for anything not in the mapping, so a non-metric row (location type)
 * simply renders no tag rather than claiming a geography.
 */
export function levelForTractField(field: keyof CensusTract): GeoLevel | null {
  for (const g of INPUT_GROUPS) {
    for (const v of g.variables) if (v.tractField === field) return v.level
  }
  return null
}

// ---- Formatting -----------------------------------------------------------

export function formatValue(v: number | null | undefined, kind: FormatKind): string {
  if (v == null || Number.isNaN(v)) return '—' // U+2014 typographic em dash
  switch (kind) {
    case 'percent':
      return `${(v * 100).toFixed(1)}%`
    case 'currency':
      if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
      if (v >= 1_000) return `$${Math.round(v / 1_000)}k`
      return `$${Math.round(v).toLocaleString()}`
    case 'number':
      return Math.round(v).toLocaleString()
    case 'decimal':
      return v.toFixed(2)
    case 'miles':
      return `${v.toFixed(2)} mi`
    case 'minutes':
      return `${v.toFixed(1)} min`
    case 'per1k':
      return v.toFixed(1)
    case 'density':
      return Math.round(v).toLocaleString()
    case 'raw':
    default:
      return String(v)
  }
}

/**
 * The one-decimal display form for an Opportunity sub-index (Demand/Market Pressure/Pricing
 * Power/Gap). This is the single source of truth for how those four values are shown, used by
 * BOTH the compact ScorePanel and the PDF one-pager so the on-screen number and the printed
 * number can never drift (T-260). An integer renders as "x.0" (e.g. 71 → "71.0"); a negative
 * Gap keeps its leading minus (e.g. -16.3); a missing value renders the em dash rather than "NaN".
 * The headline Opportunity Score is deliberately NOT formatted here — it stays Math.round.
 */
export function formatSubIndex(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—' // U+2014 typographic em dash
  return v.toFixed(1)
}

// ---- Value color-coding (T-166 / BEAD-0072) -------------------------------

/**
 * For a metric flagged `colorBasis: "share100"` — a genuine full-range 0–100% proportion —
 * return the direction-adjusted 0–100 value that `scoreColor` bands good/mid/bad. A "lower"
 * direction inverts (100 − pct) so "less is better" reads green. Share metrics are stored as
 * 0–1, so we scale to 0–100 and clamp.
 *
 * Returns `null` for any metric WITHOUT `colorBasis` (absolute magnitudes, job shares, and the
 * no-stations-confounded port shares) and for null/NaN values — those stay visually neutral.
 * This is the whole judgment: we never invent a threshold for a value that has no defensible
 * fixed banding.
 */
export function favorableShare(
  v: InputVariable,
  rawValue: number | null | undefined,
): number | null {
  if (v.colorBasis !== 'share100' || rawValue == null || Number.isNaN(rawValue)) return null
  const pct = Math.max(0, Math.min(100, rawValue * 100))
  return v.direction === 'lower' ? 100 - pct : pct
}

// ---- Percentile helpers ---------------------------------------------------

/**
 * English ordinal suffix for a whole number ("st"/"nd"/"rd"/"th").
 * Handles the 11–13 teen exception (11th, 12th, 13th) at every hundred.
 */
export function ordinalSuffix(n: number): string {
  const abs = Math.abs(Math.round(n))
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 13) return 'th'
  switch (abs % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export function pctileLabel(p: number | null): string {
  if (p == null) return '—' // U+2014 typographic em dash
  const r = Math.round(p)
  const ord = `${r}${ordinalSuffix(r)}`
  if (r >= 90) return `${ord} — top decile`
  if (r >= 75) return `${ord} — top quartile`
  if (r >= 50) return `${ord} — above median`
  if (r >= 25) return `${ord} — below median`
  return `${ord} — bottom quartile`
}

/** Map a CensusTract's dominantType to one of the INPUT_GROUPS keys. */
export function dominantTypeToGroup(dominantType: string): GroupKey | null {
  switch (dominantType) {
    case 'Corridor':
      return 'CS'
    case 'Retail':
      return 'RDS'
    case 'Suburban_SFH':
      return 'HCO'
    case 'Residential_MFH':
      return 'HCC'
    case 'Non-Residential':
      return 'NONRES'
    default:
      return null
  }
}
